import { DownloadOutlined } from '@ant-design/icons';
import { Button, Form, message } from 'antd';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModuleListDataFragment } from '../../../api/fragments/ModuleListData';
import { useGetModulesListQuery, useListModulesForApplicationDownloadQuery } from '../../../api/queries';
import { ApplicationStage, ModuleRole } from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useCompany, useCompanyContext } from '../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import { useLanguage } from '../../../components/contexts/LanguageContextManager';
import DownloadModal, { type DownloadState } from '../../../components/fields/DownloadModal/DownloadModal';
import { usePasswordModal } from '../../../components/fields/DownloadModal/PasswordModal';
import useDownloadOptions from '../../../components/fields/DownloadModal/useDownloadOptions';
import LoadingElement from '../../../components/LoadingElement';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { ExportFormat, exportApplications } from '../../../utilities/export';
import MobilityBookingList from '../../admin/MobilityBookingListPage/MobilityBookingList';
import ApplicationList, { ApplicationColumns } from '../ApplicationList';
import { getApplicationColumns } from '../ApplicationList/helpers';

export type ListPageInnerProps = {
    namespace: string;
    stage: ApplicationStage;
};

const allowedCapFormatTypes = new Set<ModuleListDataFragment['__typename']>([
    'FinderApplicationPublicModule',
    'FinderApplicationPrivateModule',
    'EventApplicationModule',
    'ConfiguratorModule',
]);

const allOption = { label: 'All', value: 'all' };

const initialValues: DownloadState = {
    module: null,
    period: { start: undefined, end: undefined },
    format: ExportFormat.DefaultFormat,
};

const ListPageInner = ({ namespace, stage }: ListPageInnerProps) => {
    const [visible, setVisible] = useState(false);
    const [formatVisible, setFormatVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [moduleListByApplication, setModuleListByApplication] = useState<string[]>([]);

    const company = useCompany(true);
    const passwordModal = usePasswordModal();
    const { t } = useTranslation(namespace);
    const { token } = useAccountContext();
    const { companies } = useCompanyContext();
    const { dealerIds } = useMultipleDealerIds();
    const { formatOptions } = useDownloadOptions();
    const language = useLanguage();

    const setDefault = useCallback(() => {
        setVisible(false);
        setFormatVisible(false);
        setDownloading(false);
    }, [setVisible, setFormatVisible, setDownloading]);

    const listModulesForApplicationDownload = useListModulesForApplicationDownloadQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { companyIds: company ? [company.id] : [], stage } },
    });

    const modules = listModulesForApplicationDownload?.data?.modules;
    const listModulesForApplicationDownloadLoading = listModulesForApplicationDownload?.loading;

    const { data, loading } = useGetModulesListQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyIds: companies.map(company => company.id),
                moduleRole: stage === ApplicationStage.Mobility ? ModuleRole.Mobility : ModuleRole.Application,
            },
        },
    });

    const moduleIds = useMemo(() => (data?.modules?.items || []).map(module => module.id), [data]);

    const applicationModuleIds = useMemo(
        () => (stage === ApplicationStage.Mobility ? moduleIds : moduleListByApplication),
        [moduleIds, moduleListByApplication, stage]
    );

    const channelModuleOption = useMemo(() => {
        const options = (modules || [])
            .filter(
                module =>
                    applicationModuleIds.includes(module.id) &&
                    (module.__typename === 'StandardApplicationModule' ||
                        module.__typename === 'EventApplicationModule' ||
                        module.__typename === 'ConfiguratorModule' ||
                        module.__typename === 'MobilityModule' ||
                        module.__typename === 'FinderApplicationPublicModule' ||
                        module.__typename === 'FinderApplicationPrivateModule' ||
                        module.__typename === 'SdmModule' ||
                        module.__typename === 'AudiFinancingModule' ||
                        module.__typename === 'BmwModule')
            )
            .map(module => ({ label: module.displayName, value: module.id }));

        return [...(company && options.length > 1 ? [allOption] : []), ...options];
    }, [applicationModuleIds, company, modules]);

    useEffect(() => {
        if (channelModuleOption?.length === 1) {
            const selectedModuleId = channelModuleOption[0].value;

            const foundModule = modules.find(module => module.id === selectedModuleId);
            if (foundModule && allowedCapFormatTypes.has(foundModule.__typename)) {
                setFormatVisible(true);
            }
        }
    }, [modules, channelModuleOption, visible]);

    const isAllCapModules = useMemo(
        () => (modules || []).every(module => allowedCapFormatTypes.has(module.__typename)),
        [modules]
    );

    const onChannelChange = useCallback(
        (value: string) => {
            const foundModule = modules.find(module => module.id === value);
            if (allowedCapFormatTypes.has(foundModule?.__typename) || (value === allOption.value && isAllCapModules)) {
                setFormatVisible(true);
            } else {
                setFormatVisible(false);
            }
        },
        [isAllCapModules, modules]
    );

    const onSubmit = useCallback(
        async (values, actions) => {
            if (!values.module) {
                return;
            }
            try {
                setDownloading(true);

                const moduleIds =
                    values.module === allOption.value
                        ? channelModuleOption
                              .filter(option => option.value !== allOption.value)
                              .map(({ value }) => value)
                        : [values.module];

                // all modules are on the same company
                const selectedModule = modules.find(data => data.id === moduleIds[0]);

                const password = await exportApplications({
                    dealerIds,
                    stage,
                    moduleIds,
                    period: values.period,
                    companyName: selectedModule.company.displayName,
                    token,
                    format: values.format,
                    languageId: language?.currentLanguageId,
                });

                if (password) {
                    passwordModal.open(password);
                }
            } catch (error) {
                message.error(t('ciModal:messages.noRecordToDownload'));
            } finally {
                // Pre-select module if there is no other choice
                if (channelModuleOption.length !== 1) {
                    actions.resetForm();
                } else {
                    actions.resetForm({
                        values: {
                            module: values.module,
                            period: initialValues.period,
                            format: initialValues.format,
                        },
                    });
                }
                setDefault();
            }
        },
        [channelModuleOption, dealerIds, modules, setDefault, passwordModal, stage, t, token]
    );

    const openDownloadModal = useCallback(() => {
        setVisible(true);
    }, [setVisible]);

    const extra = (
        <Button icon={<DownloadOutlined />} onClick={openDownloadModal} type="primary">
            {t('actions.download')}
        </Button>
    );

    const showColumns = useMemo(() => {
        const columnList = getApplicationColumns(stage);

        if (!company) {
            columnList.unshift(ApplicationColumns.Company);
        }

        return columnList;
    }, [company, stage]);

    if (listModulesForApplicationDownloadLoading) {
        return <LoadingElement />;
    }

    return (
        <ConsolePageWithHeader extra={extra} title={t('title')}>
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form id="downloadModalForm" name="downloadModalForm" onSubmitCapture={handleSubmit}>
                        <DownloadModal
                            channelModuleOption={channelModuleOption}
                            downloading={downloading}
                            formatOptions={formatOptions}
                            formatVisible={formatVisible}
                            loading={loading}
                            namespace={namespace}
                            onChannelChange={onChannelChange}
                            passwordModal={passwordModal}
                            setDefault={setDefault}
                            visible={visible}
                            forAdmin
                        />
                    </Form>
                )}
            </Formik>
            {stage === ApplicationStage.Mobility ? (
                <MobilityBookingList moduleIds={moduleIds} />
            ) : (
                <ApplicationList
                    dealerIds={dealerIds}
                    moduleIds={moduleIds}
                    setModuleListByApplication={setModuleListByApplication}
                    showColumns={showColumns}
                    stage={stage}
                />
            )}
        </ConsolePageWithHeader>
    );
};
export default ListPageInner;
